import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  forwardRef
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { CommonModule } from "@angular/common";
import { MatIconModule } from "@angular/material/icon";

@Component({
  selector: "star-rating-control",
  standalone: true,
  imports: [CommonModule, MatIconModule],
  template: `
    <div class="star-rating" [class.read-only]="readOnly">
      <span
        *ngFor="let star of stars; let i = index"
        class="star"
        [class.filled]="i < currentRating"
        [class.half]="
          showHalfStar &&
          i === Math.floor(currentRating) &&
          currentRating % 1 !== 0
        "
        [class.hoverable]="hoverEnabled && !readOnly"
        (click)="!readOnly && onStarClick(i + 1)"
        (mouseenter)="hoverEnabled && !readOnly && onStarHover(i + 1)"
        (mouseleave)="hoverEnabled && !readOnly && onMouseLeave()"
        [style.font-size.px]="size"
      >
        <mat-icon fontSet="material-symbols-outlined">
          {{
            i < currentRating
              ? "star"
              : showHalfStar &&
                i === Math.floor(currentRating) &&
                currentRating % 1 !== 0
              ? "star_half"
              : "star"
          }}
        </mat-icon>
      </span>
    </div>
  `,
  styles: [
    `
      .star mat-icon {
        font-variation-settings: "FILL" 1, "wght" 400, "GRAD" 0, "opsz" 24;
      }
      .star-rating {
        display: inline-flex;
        align-items: center;
      }

      .star {
        cursor: pointer;
        color: #ddd;
        transition: color 0.2s ease;
        display: inline-flex;
        align-items: center;
      }

      .star.filled {
        color: #ffc107;
      }

      .star.half {
        color: #ffc107;
      }

      .star.hoverable:hover {
        color: #ffb300;
      }

      .read-only .star {
        cursor: default;
      }

      .star mat-icon {
        font-size: inherit;
        width: 1em;
        height: 1em;
      }
    `
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => StarRatingComponent),
      multi: true
    }
  ]
})
export class StarRatingComponent
  implements ControlValueAccessor, OnInit, OnChanges
{
  @Input() rating: number = 0;
  @Input() readOnly: boolean = false;
  @Input() hoverEnabled: boolean = true;
  @Input() totalStars: number = 5;
  @Input() showHalfStar: boolean = false;
  @Input() size: number = 24;

  @Output() ratingChange = new EventEmitter<number>();
  @Output() ratingClicked = new EventEmitter<number>();
  @Output() ratingHovered = new EventEmitter<number>();

  stars: number[] = [];
  currentRating: number = 0;
  hoverRating: number = 0;

  private onChange = (value: number) => {};
  private onTouched = () => {};

  ngOnInit() {
    this.stars = Array(this.totalStars)
      .fill(0)
      .map((_, i) => i);
    this.currentRating = this.rating;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes["rating"] && !changes["rating"].isFirstChange()) {
      this.currentRating = this.rating;
    }
    if (changes["totalStars"]) {
      this.stars = Array(this.totalStars)
        .fill(0)
        .map((_, i) => i);
    }
  }

  onStarClick(rating: number) {
    if (!this.readOnly) {
      this.currentRating = rating;
      this.rating = rating;
      this.onChange(rating);
      this.onTouched();
      this.ratingChange.emit(rating);
      this.ratingClicked.emit(rating);
    }
  }

  onStarHover(rating: number) {
    if (!this.readOnly && this.hoverEnabled) {
      this.hoverRating = rating;
      this.currentRating = rating;
      this.ratingHovered.emit(rating);
    }
  }

  onMouseLeave() {
    if (!this.readOnly && this.hoverEnabled) {
      this.currentRating = this.rating;
      this.hoverRating = 0;
    }
  }

  // ControlValueAccessor implementation
  writeValue(value: number): void {
    if (value !== undefined && value !== null) {
      this.rating = value;
      this.currentRating = value;
    }
  }

  registerOnChange(fn: (value: number) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.readOnly = isDisabled;
  }
}
